import { useMemo } from 'react';
import { Address } from 'viem';
import { mainnet } from 'viem/chains';
import { useConnection, useReadContract } from 'wagmi';

import { TOKENS_ADDRESS_ARR } from '@/app/shared/constants/tokens';
import { getVaultContracts } from '@/app/shared/constants/web3/chainContracts';
import { ABI } from '@/app/shared/constants/web3/constants';
import { useBalances } from '@/app/shared/hooks/blockchain/useBalances';
import {
  SequentialStepPlan,
  useSendSequentialSteps,
} from '@/app/shared/hooks/blockchain/useSendSequentialSteps/useSendSequentialSteps';

interface UseClaimPendingWithdrawalArgs {
  trancheAddress?: Address;
  vaultAddress?: Address;
  epochId?: string;
  amountMzusd: bigint;
  enabled?: boolean;
  onStepSuccess?: (stepId: string) => void;
}

export const useClaimPendingWithdrawal = ({
  trancheAddress,
  vaultAddress,
  epochId,
  amountMzusd,
  enabled = true,
  onStepSuccess,
}: UseClaimPendingWithdrawalArgs) => {
  const { address, isConnected, chainId } = useConnection();
  const { refetch: refetchWalletBalances } = useBalances({
    tokens: TOKENS_ADDRESS_ARR,
    address,
    enabled: false,
  });

  const contracts =
    getVaultContracts(chainId, vaultAddress) ?? getVaultContracts(mainnet.id, vaultAddress);

  const { data: epochInfo, refetch: refetchEpochInfo } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'getUserEpochInfo',
    args: address && epochId ? [address, BigInt(epochId)] : undefined,
    query: {
      enabled: enabled && !!address && !!trancheAddress && !!epochId,
    },
  });

  const claimableAssets = (() => {
    if (epochInfo == null) return undefined;
    if (Array.isArray(epochInfo)) return epochInfo[1] as bigint;
    if (typeof epochInfo === 'object' && 'claimableAssets' in epochInfo) {
      return (epochInfo as { claimableAssets: bigint }).claimableAssets;
    }
    return undefined;
  })();

  const isClaimComplete = claimableAssets === 0n;

  const plannedSteps = useMemo((): SequentialStepPlan[] => {
    if (
      !enabled ||
      !address ||
      !trancheAddress ||
      !contracts?.MZUSD ||
      !epochId ||
      amountMzusd <= 0n ||
      claimableAssets === undefined
    ) {
      return [];
    }

    return [
      {
        id: 'claim-tranche',
        title: 'Claim MZUSD',
        description: 'Claim MZUSD from the tranche',
        isComplete: isClaimComplete,
        tx: {
          address: trancheAddress,
          abi: ABI.TRANCHE,
          functionName: 'claim',
          args: [address, BigInt(epochId)],
        },
      },
      {
        id: 'redeem-mzusd',
        title: 'Redeem MZUSD',
        description: 'Redeem MZUSD to receive USDC',
        isComplete: false,
        tx: {
          address: contracts.MZUSD,
          abi: ABI.MZUSD,
          functionName: 'requestWithdrawal',
          args: [amountMzusd],
        },
      },
    ];
  }, [
    address,
    amountMzusd,
    claimableAssets,
    contracts?.MZUSD,
    enabled,
    epochId,
    isClaimComplete,
    trancheAddress,
  ]);

  const planKey = `${trancheAddress ?? ''}:${epochId ?? ''}:${amountMzusd.toString()}:${address ?? ''}`;

  const sequential = useSendSequentialSteps({
    steps: plannedSteps,
    planKey,
    enabled: enabled && isConnected && plannedSteps.length > 0,
    onStepSuccess: onStepSuccess ? (stepId) => onStepSuccess(stepId) : undefined,
  });

  const isReady =
    enabled &&
    isConnected &&
    !!address &&
    !!trancheAddress &&
    !!contracts?.MZUSD &&
    !!epochId &&
    amountMzusd > 0n &&
    plannedSteps.length > 0 &&
    claimableAssets !== undefined;

  return {
    ...sequential,
    isReady,
    refetchWalletBalances,
    refetchEpochInfo,
    isBatchingTransaction: false as const,
  };
};
